/* ─────────────────────────────────────────────────────────────────────────────
   GrowthOS — HubSpot Review / Decision-Insights kit: the shared GrowthOS
   DataTable / Panel (growthos-shared/GxPagedTable) in these pages' look, the
   shared formatters, HubSpot record deep-links and the audit tiles / badges.
   ──────────────────────────────────────────────────────────────────────────── */
import React from 'react'
import { Box, Paper, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { GX_BORDER, GX_CARD_SX, GX_COLORS, GX_INK_500, GX_INK_900 } from '../growthos-shared/GrowthOsUi'
import { GxCollapsePanel, GxExpandableTable, GxPagedTable } from '../growthos-shared/GxPagedTable'
import { integer } from '../growthos-shared/gxFormat'

export { integer, currency, shortDate, daysAgoLabel, GrowthPct } from '../growthos-shared/gxFormat'

export const GX_INK_400 = '#94a3b8'

// ── HubSpot deep-links (portal + UI domain come from the API payload) ───────
// 0-1 = contact, 0-2 = company, 0-3 = deal.
export const hsRecordUrl = (hub, obj, id) => (hub && id ? `https://${hub.uiDomain}/contacts/${hub.portalId}/record/${obj}/${id}` : undefined)
export const hsCompanySearchUrl = (hub, q) => (hub ? `https://${hub.uiDomain}/contacts/${hub.portalId}/objects/0-2/views/all/list?query=${encodeURIComponent(q)}` : undefined)

export function HsLink({ href, label = 'Open' }) {
  if (!href) return '—'
  return (
    <Box component="a" href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12, fontWeight: 600, color: GX_COLORS.indigo, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      {label} <OpenInNewIcon sx={{ fontSize: 12 }} />
    </Box>
  )
}

// The shared GrowthOS table / panel in this page pair's look.
export const DataTable = (props) => <GxPagedTable variant="hubspot" {...props} />
export const ExpandableTable = (props) => <GxExpandableTable variant="hubspot" {...props} />
export const Panel = (props) => <GxCollapsePanel variant="hubspot" {...props} />

const TILE_TONES = { indigo: '#6366f1', teal: '#0d9488', amber: '#d97706', rose: '#e11d48' }

/** Audit tile (original HubSpot Review Tile): tone top bar, optional chevron + active ring when clickable. */
export function Tile({ label, value, sub, tone = 'indigo', active, onClick }) {
  const bar = TILE_TONES[tone] || TILE_TONES.indigo
  return (
    <Paper variant="outlined" component={onClick ? 'button' : 'div'} onClick={onClick}
      sx={{
        ...GX_CARD_SX, p: 2, textAlign: 'left', borderTop: `3px solid ${bar}`, fontFamily: 'inherit', width: '100%',
        cursor: onClick ? 'pointer' : 'default',
        boxShadow: active ? `0 0 0 2px #818cf8, ${GX_CARD_SX.boxShadow}` : GX_CARD_SX.boxShadow,
        '&:hover': onClick ? { boxShadow: `0 4px 14px rgba(15,23,42,0.12)${active ? ', 0 0 0 2px #818cf8' : ''}` } : undefined,
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: GX_INK_400 }}>{label}</Typography>
        {onClick && <ExpandMoreIcon sx={{ fontSize: 16, color: GX_INK_400, transition: 'transform .15s', transform: active ? 'rotate(180deg)' : 'none' }} />}
      </Box>
      <Typography sx={{ mt: 0.5, fontSize: 24, fontWeight: 700, color: GX_INK_900, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Typography sx={{ mt: 0.25, fontSize: 12, color: GX_INK_400 }}>{sub}</Typography>}
    </Paper>
  )
}

/** Bordered drill container rendered under a row of tiles. */
export function DrillBox({ children }) {
  return <Box sx={{ border: `1px solid ${GX_BORDER}`, borderRadius: '12px', mb: 2, bgcolor: '#fff' }}>{children}</Box>
}

export function Clean({ text = 'None — clean on this check.' }) {
  return <Typography sx={{ p: 2, textAlign: 'center', fontSize: 13, color: GX_INK_400 }}>{text}</Typography>
}

/** Small clickable count used inside tables to open a (row × metric) detail. */
export function CountButton({ value, active, onClick }) {
  if (!value) return <span style={{ color: GX_INK_400 }}>0</span>
  return (
    <Box component="button" onClick={(e) => { e.stopPropagation(); onClick() }}
      sx={{ border: 'none', bgcolor: 'transparent', p: 0, cursor: 'pointer', fontWeight: 600, fontSize: 12.5, color: '#4338ca', textDecoration: active ? 'underline' : 'none', fontFamily: 'inherit', '&:hover': { textDecoration: 'underline' } }}>
      {integer(value)}
    </Box>
  )
}

export function Badge({ children, color = GX_INK_500, bg }) {
  return (
    <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.25, borderRadius: '9999px', fontSize: 11, fontWeight: 600, color, bgcolor: bg || `${color}14` }}>{children}</Box>
  )
}
